package dev.lingvo.site.components.automation

import com.varabyte.kobweb.navigation.Router
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.ToastState
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tr

/**
 * Where a run's receipt lives.
 *
 * One function rather than a template repeated at every call site, because six surfaces now start a
 * run and every one of them has to be able to send the reader to the same page afterwards. A run
 * whose receipt cannot be found is a charge with no invoice.
 */
fun jobReceiptHref(projectSlug: String, jobId: String): String =
    "/app/projects/$projectSlug/data/jobs/$jobId"

/**
 * The receipt half of "estimate before, receipt after".
 *
 * **The action is the whole point.** A toast that says a run started and then disappears leaves the
 * reader on the page they were on with no way back to what they just spent money on — the run id is
 * never shown, so there is nothing to search for either. `showWithAction` waits to be dismissed, so
 * the door stays open until somebody closes it.
 *
 * Written with `tr` rather than `t` on purpose: every caller reaches this from a click handler or a
 * coroutine, which is not composition.
 *
 * @return the toast id, so a caller that wants to replace its own earlier message can dismiss it.
 */
fun showJobToast(
    toasts: ToastState,
    router: Router,
    projectSlug: String,
    jobId: String,
    message: String,
    variant: LToastVariant = LToastVariant.INFO,
): Int = toasts.showWithAction(
    message = message,
    actionLabel = tr("automation.job_toast.open_receipt", "Open receipt"),
    onAction = { router.tryRoutingTo(jobReceiptHref(projectSlug, jobId)) },
    variant = variant,
)

/**
 * "Pre-translating 412 strings · Open receipt".
 *
 * The count is the run's real scope as the server priced it, never a rounded or invented number:
 * `PRODUCT.md` principle 4, and the one thing that makes the sentence worth reading at all.
 */
fun showPretranslateStarted(
    toasts: ToastState,
    router: Router,
    projectSlug: String,
    jobId: String,
    strings: Int,
): Int = showJobToast(
    toasts = toasts,
    router = router,
    projectSlug = projectSlug,
    jobId = jobId,
    message = tPlural(
        "automation.job_toast.count.pre_translating",
        strings,
        "Pre-translating {count} string",
        "Pre-translating {count} strings",
    ),
    variant = LToastVariant.INFO,
)

/**
 * The message for a run that was already going when this surface asked for one.
 *
 * A duplicate press is not an error — the work the reader wanted is already happening — so it
 * reports the truth and hands over the same receipt door rather than a refusal.
 */
fun showJobAlreadyRunning(
    toasts: ToastState,
    router: Router,
    projectSlug: String,
    jobId: String,
): Int = showJobToast(
    toasts = toasts,
    router = router,
    projectSlug = projectSlug,
    jobId = jobId,
    message = tr(
        "automation.job_toast.a_run_is_already_going_in",
        "A run is already going in this project.",
    ),
    variant = LToastVariant.INFO,
)
